<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Product List</h5>
          <p class="page-sub">Manage your inventory products</p>
        </div>
        <div class="page-actions">
  
          <!-- Search -->
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input type="text" class="search-input" placeholder="Search products..." v-model="query" />
          </div>
  
          <!-- Export -->
          <button class="btn-outline" @click="openExportModal('csv')" :disabled="exportLoading">
            <font-awesome-icon :icon="exportLoading ? 'spinner' : 'file-csv'" :spin="exportLoading" /> CSV
          </button>
          <button class="btn-outline" @click="openExportModal('pdf')" :disabled="exportLoading">
            <font-awesome-icon :icon="exportLoading ? 'spinner' : 'file-pdf'" :spin="exportLoading" /> PDF
          </button>
  
          <!-- Bulk Upload -->
          <router-link
            :to="$url('/bulk-product-upload')"
            class="btn-outline"
            :class="permission.create_product == 0 ? 'disabled' : ''"
          >
            <font-awesome-icon icon="upload" /> Bulk Upload
          </router-link>
  
          <!-- Create -->
          <router-link
            :to="$url('/create-product')"
            class="btn-primary-action"
            :class="permission.create_product == 0 ? 'disabled' : ''"
          >
            <font-awesome-icon icon="plus" /> Create Product
          </router-link>
  
          <!-- Bulk Delete -->
          <button
            class="btn-danger-action"
            :data-bs-toggle="permission.delete_product == 1 ? 'modal' : ''"
            data-bs-target="#bulkModal"
            :disabled="permission.delete_product != 1 || selectedModel.length === 0"
          >
            <font-awesome-icon icon="trash" /> Bulk Delete
            <span v-if="selectedModel.length > 0" class="badge-count">{{ selectedModel.length }}</span>
          </button>
  
        </div>
      </div>
  
      <!-- Data Card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted">Loading products...</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="details.length === 0" class="state-block">
          <div class="state-icon state-icon--blue">
            <font-awesome-icon icon="boxes" />
          </div>
          <p class="state-title">No products yet</p>
          <p class="state-sub">Start by adding your first product to inventory.</p>
          <router-link v-if="permission.create_product != 0" :to="$url('/create-product')" class="btn-primary-action mt-2">
            <font-awesome-icon icon="plus" /> Create Product
          </router-link>
        </div>
  
        <!-- Table -->
        <template v-else>
  
          <div class="table-toolbar">
            <span class="table-count">
              Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> products
            </span>
            <div class="d-flex align-items-center gap-2">
              <label class="table-count">Rows:</label>
              <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option :value="pagination.total">All</option>
              </select>
            </div>
          </div>
  
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th class="col-check">
                    <input type="checkbox" v-model="allSelected" @change="selectAll" class="form-check-input" />
                  </th>
                  <th style="width:28%">Product Name</th>
                  <th>Qty</th>
                  <th>Pcs/Carton</th>
                  <th>Cartons</th>
                  <th>Pcs</th>
                  <th>Cost ({{ company?.currency?.symbol }})</th>
                  <th>Selling ({{ company?.currency?.symbol }})</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, index) in details" :key="index" :class="selectedModel.includes(product.id) ? 'row-selected' : ''">
                  <td class="col-check">
                    <input type="checkbox" v-model="selectedModel" :value="product.id" class="form-check-input" />
                  </td>
  
                  <td>
                    <div class="product-cell">
                      <div class="product-avatar">
                        <font-awesome-icon icon="box" />
                      </div>
                      <span class="product-name">{{ product.product_name }}</span>
                    </div>
                  </td>
  
                  <td>
                    <span class="qty-badge" :class="(product.store_qty ?? 0) < 5 ? 'qty-low' : ''">
                      {{ product.store_qty ?? 0 }}
                    </span>
                  </td>
  
                  <td class="text-muted-cell">
                    {{ product.variants.filter(v => v.variant_selling_price == Math.max.apply(Math, product.variants.map(v2 => v2.variant_selling_price)))[0]?.variant_quantity ?? 'N/A' }}
                    {{ calculatePieces(product) }}
                  </td>
  
                  <td class="text-muted-cell">
                    {{ carton[product.id] === undefined ? 'N/A' : carton[product.id] }}
                  </td>
  
                  <td class="text-muted-cell">
                    {{ pieces[product.id] === undefined ? 'N/A' : pieces[product.id] }}
                  </td>
  
                  <td class="text-muted-cell">{{ $filters.formatMoney(product.cost_price) }}</td>
                  <td class="text-muted-cell fw-semibold" style="color:#004AAD">{{ $filters.formatMoney(product.selling_price) }}</td>
  
                  <td class="col-actions">
                    <div class="action-btns">
                      <router-link
                        :to="permission.view_product == 1 ? $url(`/view-product/${product.id}`) : '#'"
                        class="action-btn action-btn--view"
                        :class="permission.view_product != 1 ? 'disabled' : ''"
                        title="View product"
                      >
                        <font-awesome-icon icon="eye" />
                      </router-link>
                      <router-link
                        :to="permission.edit_product == 1 ? $url(`/edit-product/${product.id}`) : '#'"
                        class="action-btn action-btn--edit"
                        :class="permission.edit_product != 1 ? 'disabled' : ''"
                        title="Edit product"
                      >
                        <font-awesome-icon icon="pen" />
                      </router-link>
                      <button
                        class="action-btn action-btn--delete"
                        @click.prevent="getModalCurrentId(product.id)"
                        :data-bs-toggle="permission.delete_product == 1 ? 'modal' : ''"
                        data-bs-target="#singleModal"
                        :disabled="permission.delete_product != 1"
                        title="Delete product"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer">
            <pagination
              :pagination="pagination"
              @prev="tableData.page = pagination.currentPage - 1; fetchData()"
              @next="tableData.page = pagination.currentPage + 1; fetchData()"
            />
          </div>
  
        </template>
      </div>
  
      <SingleDeleteModal :modalId="'singleModal'" @deleteSingle="callSingleProductDeleteButton" :modalText="'Are you sure you want to delete this product?'" :action="'deleteSingle'" />
      <BulkDeleteModal :modalId="'bulkModal'" @deleteBulk="callBulkDeleteProductButton" :modalText="'Are you sure you want to delete the selected products?'" />

      <!-- Export Column Picker -->
      <transition name="fade">
        <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
          <div class="col-picker-modal">
            <div class="col-picker-header">
              <div>
                <h3 class="col-picker-title">Select Columns</h3>
                <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
              </div>
              <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
            </div>
            <div class="col-picker-actions">
              <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
              <span class="col-picker-sep">·</span>
              <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
              <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
            </div>
            <div class="col-picker-grid">
              <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
                <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
                <span class="col-picker-label">{{ col.label }}</span>
              </label>
            </div>
            <div class="col-picker-footer">
              <button class="btn-outline" @click="showExportModal = false">Cancel</button>
              <button class="btn-export-primary" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
                <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
                Export {{ pendingExportFmt.toUpperCase() }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </template>
  
  <script>
  import InventoryHeaderTab from "./component/inventoryheadertab.vue";
  import axios from "axios";
  import { useToast } from "vue-toastification";
  import SingleDeleteModal from "../../../components/ActionModal.vue";
  import BulkDeleteModal from "../../../components/bulkDeleteModal.vue";
  import { reusableMixin } from "../../../lib/reusableMixin";
  import jsPDF from "jspdf";
  import "jspdf-autotable";
  import { useStore } from "vuex";
  
  export default {
    components: { InventoryHeaderTab, SingleDeleteModal, BulkDeleteModal },
    mixins: [reusableMixin],
  
    data() {
      return {
        headerArrayColumn: ['S/N','Product Name','Qty','Pcs Per Carton','Cartons','Pcs','Cost Price','Selling Price'],
        print_content: [], details: [], loading: false, query: null,
        selectedModel: [], allSelected: false,
        tableData: { page: 1, draw:0, length:5, search:'', column:0, dir:'desc', id:'', status:[0,1,2,3], query:'' },
        pagination: { lastPage:'', currentPage:'', total:'', lastPageUrl:'', nextPageUrl:'', prevPageUrl:'', from:'', to:'' },
        toast: useToast(), currentID: null, store: useStore(),
        carton: [], pieces: [],
        showExportModal: false, pendingExportFmt: 'csv', selectedExportCols: [], exportLoading: false,
        exportColumnDefs: [
          { key: 'product_name',  label: 'Product Name',   pdfWidth: 180 },
          { key: 'qty',           label: 'Qty',            pdfWidth: 70  },
          { key: 'pcs_per_carton',label: 'Pcs / Carton',   pdfWidth: 90  },
          { key: 'cartons',       label: 'Cartons',        pdfWidth: 80  },
          { key: 'pcs',           label: 'Pcs',            pdfWidth: 70  },
          { key: 'cost_price',    label: 'Cost Price',     pdfWidth: 136 },
          { key: 'selling_price', label: 'Selling Price',  pdfWidth: 135 },
        ],
      };
    },
  
    computed: {
      permission() { return this.store.state.getCurrentUserPermission; },
    },
  
    watch: { query() { this.fetchData(); } },
    created() { this.fetchData(); this.getCompany(); },
  
    methods: {
      openExportModal(format) {
        this.pendingExportFmt   = format;
        this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
        this.showExportModal    = true;
      },

      getRowValue(product, key) {
        const variants = product.variants ?? [];
        const maxVariant = variants.length
          ? variants.reduce((a, b) => b.variant_selling_price > a.variant_selling_price ? b : a, variants[0])
          : null;
        const pcsPerCarton = maxVariant?.variant_quantity ?? 'N/A';
        let cartons = 'N/A', pcs = 'N/A';
        if (variants.length && pcsPerCarton !== 'N/A') {
          let qty = parseInt(product.store_qty ?? product.quantity ?? 0);
          let c = 0;
          while (parseInt(pcsPerCarton) < qty) { qty -= parseInt(pcsPerCarton); c++; if (qty < parseInt(pcsPerCarton)) break; }
          cartons = c; pcs = qty;
        }
        switch (key) {
          case 'product_name':   return product.product_name || '—';
          case 'qty':            return product.store_qty ?? product.quantity ?? 0;
          case 'pcs_per_carton': return pcsPerCarton;
          case 'cartons':        return cartons;
          case 'pcs':            return pcs;
          case 'cost_price':     return product.cost_price ?? '—';
          case 'selling_price':  return product.selling_price ?? '—';
          default:               return '—';
        }
      },

      async doExport() {
        if (!this.selectedExportCols.length) return;
        this.exportLoading = true; this.showExportModal = false;
        try {
          const params = { ...this.tableData, page: 1, length: 10000, query: this.query };
          const res  = await axios.get('/inventory/product', { params });
          const rows = res.data?.data?.data ?? [];
          const cols    = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
          const headers = cols.map(c => c.label);
          const body    = rows.map(r => cols.map(c => this.getRowValue(r, c.key)));
          const date    = new Date().toISOString().slice(0, 10);
          if (this.pendingExportFmt === 'csv') {
            const csv  = [headers, ...body].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const url  = URL.createObjectURL(blob);
            const a    = document.createElement('a'); a.href = url; a.download = `products-${date}.csv`; a.click(); URL.revokeObjectURL(url);
          } else {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
            const usable = 761, totalW = cols.reduce((s, c) => s + c.pdfWidth, 0), scale = usable / totalW;
            const colStyles = {}; cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
            doc.setFontSize(18); doc.setFont('helvetica','bold'); doc.setTextColor(26,43,69); doc.text('Product List', 40, 46);
            doc.setFontSize(9); doc.setFont('helvetica','normal'); doc.setTextColor(122,144,176);
            doc.text(`Exported ${new Date().toLocaleDateString('en-GB')}   ·   ${rows.length} record(s)`, 40, 62);
            doc.autoTable({ startY:78, head:[headers], body, styles:{fontSize:7.5,cellPadding:3.5,textColor:[26,43,69],overflow:'ellipsize'}, headStyles:{fillColor:[0,74,173],textColor:255,fontStyle:'bold',fontSize:7.5}, alternateRowStyles:{fillColor:[248,250,252]}, columnStyles:colStyles, tableWidth:usable, margin:{left:40,right:40},
              didDrawPage(d) { doc.setFontSize(8); doc.setTextColor(192,202,214); doc.text(`Page ${d.pageNumber}`, doc.internal.pageSize.width/2, doc.internal.pageSize.height-16, {align:'center'}); }
            });
            doc.save(`products-${date}.pdf`);
          }
        } catch { this.toast.error('Export failed. Please try again.'); }
        finally { this.exportLoading = false; }
      },

      dataToPrint() {
        this.print_content = this.details.map((product, idx) => ({
          sn: idx + 1,
          product_name: product.product_name,
          quantity: product.store_qty ?? product.quantity,
          pcs_per_carton: product.variants.length > 0
            ? product.variants.filter(v => v.variant_selling_price == Math.max.apply(Math, product.variants.map(v2 => v2.variant_selling_price)))[0]?.variant_quantity
            : 'N/A',
          carton: product.variants.length > 0 ? this.carton[product.id] : 'N/A',
          pcs:    product.variants.length > 0 ? this.pieces[product.id] : 'N/A',
          cost_price:    product.cost_price,
          selling_price: product.selling_price,
        }));
        this.drawPdf(this.print_content, this.headerArrayColumn, 'l', 'Product', 'Product');
      },

      exportToCSV() {
        const rows = this.details.map((product, idx) => ({
          sn: idx + 1,
          product_name: product.product_name,
          quantity: product.store_qty ?? product.quantity ?? 0,
          pcs_per_carton: product.variants.length > 0
            ? (product.variants.filter(v => v.variant_selling_price == Math.max.apply(Math, product.variants.map(v2 => v2.variant_selling_price)))[0]?.variant_quantity ?? 'N/A')
            : 'N/A',
          carton: product.variants.length > 0 ? (this.carton[product.id] ?? 'N/A') : 'N/A',
          pcs:    product.variants.length > 0 ? (this.pieces[product.id] ?? 'N/A') : 'N/A',
          cost_price:    product.cost_price ?? '',
          selling_price: product.selling_price ?? '',
        }));
        const header = this.headerArrayColumn.join(',');
        const body = rows.map(r => [r.sn, r.product_name, r.quantity, r.pcs_per_carton, r.carton, r.pcs, r.cost_price, r.selling_price].join(','));
        this._downloadCSV([header, ...body].join('\n'), `products-${this.formatDateMMDDYY()}.csv`);
      },

      calculatePieces(product) {
        const variant = product.variants;
        if (variant.length > 0) {
          let quantity    = parseInt(product.store_qty ?? product?.quantity);
          const variantQty = variant.filter(v => v.variant_selling_price == Math.max.apply(Math, variant.map(v2 => v2.variant_selling_price)))[0]?.variant_quantity;
          let carton = 0;
          while (parseInt(variantQty) < quantity) {
            quantity -= variantQty; carton += 1;
            if (quantity < variantQty) break;
          }
          this.pieces[product.id] = quantity;
          this.carton[product.id] = carton;
        }
      },
  
      selectAll() {
        this.selectedModel = this.allSelected ? this.details.map(u => u.id) : [];
      },
  
      fetchData(url = '/inventory/product') {
        this.loading = true;
        this.tableData.query = this.query;
        this.tableData.draw++;
        axios.get(url, { params: this.tableData })
          .then(response => {
            this.loading = false;
            this.details = response.data.data.data;
            this.configPagination(response.data.data);
          })
          .catch(() => { this.loading = false; });
      },
  
      callSingleProductDeleteButton() {
        axios.delete(`/inventory/product/${this.currentID}`)
          .then(() => { this.toast.success('Product deleted successfully'); this.selectedModel = []; this.fetchData(); })
          .catch(error => { this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },
  
      callBulkDeleteProductButton() {
        axios.post('/inventory/product/delete-bulk', { products: this.selectedModel })
          .then(response => { this.toast.success(response.data.data); this.selectedModel = []; this.fetchData(); })
          .catch(error => { this.toast.error(error?.response?.data?.message ?? 'Something went wrong'); });
      },
  
      getModalCurrentId(id) { this.currentID = id; },
  
      configPagination(data) {
        this.pagination.lastPage    = data.last_page;
        this.pagination.currentPage = data.current_page;
        this.pagination.total       = data.total;
        this.pagination.lastPageUrl = data.last_page_url;
        this.pagination.nextPageUrl = data.next_page_url;
        this.pagination.prevPageUrl = data.prev_page_url;
        this.pagination.from        = data.from;
        this.pagination.to          = data.to;
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 32px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  .search-input::placeholder { color:#aab8cc; }
  
  /* ── Buttons ── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
  .btn-primary-action.disabled { opacity:.5; pointer-events:none; }
  .btn-danger-action  { display:inline-flex; align-items:center; gap:7px; background:#fff0f0; color:#e03535; border:1px solid #fac5c5; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-danger-action:hover:not(:disabled) { background:#ffe0e0; }
  .btn-danger-action:disabled { opacity:.45; cursor:not-allowed; }
  .btn-outline  { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  .btn-outline::after { display:none !important; }
  .btn-outline.disabled { opacity:.5; pointer-events:none; }
  .badge-count  { display:inline-flex; align-items:center; justify-content:center; background:#e03535; color:#fff; font-size:10px; font-weight:700; width:18px; height:18px; border-radius:50%; }
  
  /* ── Data Card ── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon  { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub   { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Toolbar ── */
  .table-toolbar { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
  .table-count   { font-size:13px; color:#7a90b0; }
  .rows-select   { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }
  
  /* ── Table ── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr  { background:#f8fafc; }
  .data-table thead th  { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr  { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody tr.row-selected { background:#edf4ff; }
  .data-table tbody td  { padding:13px 16px; font-size:13.5px; vertical-align:middle; }
  .col-check   { width:44px; text-align:center; }
  .col-actions { width:110px; text-align:center; }
  .text-muted-cell { color:#4a5e78; }
  .fw-semibold { font-weight:600; }
  
  /* ── Product Cell ── */
  .product-cell   { display:flex; align-items:center; gap:10px; }
  .product-avatar { width:34px; height:34px; border-radius:8px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
  .product-name   { font-weight:600; color:#1a2b45; }
  
  /* ── Qty Badge ── */
  .qty-badge { display:inline-block; background:#edf4ff; color:#004AAD; border-radius:20px; padding:2px 10px; font-size:12.5px; font-weight:600; }
  .qty-low   { background:#fff0f0; color:#e03535; }
  
  /* ── Action Buttons ── */
  .action-btns   { display:flex; align-items:center; justify-content:center; gap:5px; }
  .action-btn    { width:30px; height:30px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; transition:background .15s,border-color .15s; text-decoration:none; background:#fff; }
  .action-btn--view   { color:#0d9488; }
  .action-btn--view:hover:not(.disabled)   { background:#f0fdfb; border-color:#a7f3d0; }
  .action-btn--edit   { color:#004AAD; }
  .action-btn--edit:hover:not(.disabled)   { background:#edf4ff; border-color:#c5d6f5; }
  .action-btn--delete { color:#e03535; }
  .action-btn--delete:hover:not(:disabled) { background:#fff0f0; border-color:#fac5c5; }
  .action-btn--delete:disabled { color:#ccc; cursor:not-allowed; border-color:#f0f0f0; }
  .action-btn.disabled { color:#ccc; pointer-events:none; }
  
  /* ── Footer ── */
  .table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }
  
  /* ── Dropdown ── */
  .dropdown-menu  { border:1px solid #e4eaf2; border-radius:9px; box-shadow:0 8px 24px rgba(0,0,0,.08); padding:6px; min-width:170px; }
  .dropdown-item  { border-radius:6px; font-size:13px; padding:8px 12px; color:#1a2b45; }
  .dropdown-item:hover { background:#f4f7fb; }
  
  /* ── Responsive ── */
  @media(max-width:767px) {
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
    .btn-primary-action,.btn-danger-action,.btn-outline { width:100%; justify-content:center; }
  }

  /* ── Column picker modal ── */
  .col-picker-backdrop { position:fixed; inset:0; background:rgba(15,25,40,.45); z-index:1050; display:flex; align-items:center; justify-content:center; padding:20px; }
  .col-picker-modal    { background:#fff; border-radius:14px; width:100%; max-width:480px; box-shadow:0 24px 60px rgba(0,0,0,.18); overflow:hidden; }
  .col-picker-header   { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 14px; border-bottom:1px solid #f0f4f9; }
  .col-picker-title    { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .col-picker-sub      { font-size:12px; color:#7a90b0; margin:0; }
  .col-picker-close    { background:none; border:none; color:#7a90b0; font-size:15px; cursor:pointer; padding:4px; line-height:1; }
  .col-picker-close:hover { color:#1a2b45; }
  .col-picker-actions  { display:flex; align-items:center; gap:8px; padding:10px 22px; background:#f8fafc; border-bottom:1px solid #f0f4f9; }
  .col-picker-link     { background:none; border:none; color:#004AAD; font-size:12px; cursor:pointer; padding:0; font-weight:600; font-family:inherit; }
  .col-picker-link:hover { text-decoration:underline; }
  .col-picker-sep      { color:#c5d3e0; }
  .col-picker-count    { margin-left:auto; color:#7a90b0; font-size:11.5px; }
  .col-picker-grid     { display:grid; grid-template-columns:1fr 1fr; gap:2px; padding:14px 16px; max-height:280px; overflow-y:auto; }
  .col-picker-item     { display:flex; align-items:center; gap:9px; padding:9px 10px; border-radius:8px; cursor:pointer; transition:background .1s; }
  .col-picker-item:hover { background:#f4f7fb; }
  .col-picker-check    { width:15px; height:15px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
  .col-picker-label    { font-size:13px; color:#1a2b45; user-select:none; }
  .col-picker-footer   { display:flex; justify-content:flex-end; gap:8px; padding:14px 20px; border-top:1px solid #f0f4f9; }
  .btn-export-primary  { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; font-family:inherit; }
  .btn-export-primary:hover:not(:disabled) { background:#003b8e; }
  .btn-export-primary:disabled { opacity:.45; cursor:not-allowed; }
  .fade-enter-active,.fade-leave-active { transition:opacity .15s ease; }
  .fade-enter,.fade-leave-to { opacity:0; }
  </style>